import { Spinner } from "@climatepartner/components/Spinner";
import { Box, Typography } from "@mui/material";
import { ReactElement, Suspense } from "react";
import { Outlet } from "react-router-dom";

import { Breadcrumbs } from "../../shared/ui/layout/breadcrumbs";
import { useNetworkCapabilities } from "./public/use-network-capabilities";
import { NetworkNotFoundRedirect, useIsElevatedFromToken } from "./shared/network-not-found-redirect";
import { useNetworkRoleUserTracking } from "./shared/use-network-role-user-tracking";
import { useSupplierInvitationStatus } from "./supplier/shared/use-supplier-invitation-status";

export {
  BuyerDetailsPage,
  BuyerProductRequestsOverviewPage,
  ConnectionDetailPage,
  ConnectionsPage,
  NetworkLandingPage,
  ProductRequestsPage,
  SupplierConnectionsPage,
} from "./lazy-pages";

/**
 * One Suspense boundary here covers the whole subtree; the nested route objects carry only `path`
 * and `handle`, so the pages need none of their own.
 */
const NetworkFrame = (): ReactElement => (
  <>
    <Breadcrumbs />
    <Suspense fallback={<Spinner />}>
      <Outlet />
    </Suspense>
  </>
);

export const NetworkLandingRoute = () => {
  const { isBuyer, isSupplier } = useNetworkCapabilities();

  if (!isBuyer && !isSupplier) {
    return <NetworkNotFoundRedirect />;
  }

  return <NetworkFrame />;
};

export const BuyerNetworkRoute = () => {
  const { isBuyer } = useNetworkCapabilities();
  // Tracked here too, not only on the landing, so a deep link or bookmark straight into the buyer
  // subtree is still counted.
  useNetworkRoleUserTracking("buyer", isBuyer);

  if (!isBuyer) {
    return <NetworkNotFoundRedirect />;
  }

  return <NetworkFrame />;
};

const SupplierNoAccessMessage = (): ReactElement => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      minHeight: "60vh",
      p: 4,
      textAlign: "center",
    }}
  >
    <Typography data-testid="supplier-no-access" variant="h5">
      You&apos;ve been invited to ClimatePartner Network
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480 }}>
      Your invitation is ready, but network access hasn&apos;t been activated for your account yet. Our team is setting
      this up — please check back soon, or contact your ClimatePartner representative if you need access sooner.
    </Typography>
  </Box>
);

/**
 * A user without access but with a pending invitation sees a dedicated message rather than a bare
 * 403, since this route is where an existing customer lands after the post-login redirect from the
 * invitation front-door.
 */
export const SupplierNetworkRoute = () => {
  const { isSupplier } = useNetworkCapabilities();
  const isElevated = useIsElevatedFromToken();
  useNetworkRoleUserTracking("supplier", isSupplier);

  const { data: invitationStatus, isLoading: isInvitationLoading } = useSupplierInvitationStatus({
    enabled: !isSupplier,
  });

  if (!isSupplier) {
    if (isInvitationLoading) {
      return <Spinner />;
    }
    // The invitation message is for customers only — an elevated employee whose email happens
    // to match a pending invitation is denied like everyone else.
    if (invitationStatus?.hasPendingInvitation && !isElevated) {
      return <SupplierNoAccessMessage />;
    }
    return <NetworkNotFoundRedirect />;
  }

  return <NetworkFrame />;
};
